import React, { useMemo, type CSSProperties } from 'react';
import { useInventoryStore } from '../../../store/inventoryStore';
import { useStockDashboardStore } from '../../../store/stockDashboardStore';
import { useWidgetSize } from './useWidgetSize';

/** Parse #rgb / #rrggbb → RGB */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.replace(/^#/, '').trim();
  if (h.length === 3) {
    return {
      r: parseInt(h[0]! + h[0]!, 16),
      g: parseInt(h[1]! + h[1]!, 16),
      b: parseInt(h[2]! + h[2]!, 16),
    };
  }
  if (h.length === 6) {
    const n = parseInt(h, 16);
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  return null;
}

const rgba = (r: number, g: number, b: number, a: number) => `rgba(${r},${g},${b},${a})`;

/** Trendy mesh / aura fill for progress bars (layered radials + diagonal wash). */
function auraMeshBarStyle(accentHex: string, isDark: boolean, salt: number): CSSProperties {
  const fallback = isDark ? { r: 96, g: 165, b: 250 } : { r: 59, g: 130, b: 246 };
  const rgb = hexToRgb(accentHex) ?? fallback;
  const { r, g, b } = rgb;

  const rHi = Math.min(255, r + 42);
  const gHi = Math.min(255, g + 28);
  const bHi = Math.max(0, b - 18);

  const rLo = Math.max(0, r - 32);
  const gLo = Math.min(255, g + 36);
  const bLo = Math.min(255, b + 52);

  const boost = isDark ? 1.08 : 1;
  const aBlob = 0.42 * boost;
  const aBlob2 = 0.48 * boost;
  const aWash = isDark ? 0.88 : 0.92;

  const ox = ((salt * 17) % 24) - 12;
  const oy = ((salt * 23) % 20) - 10;

  const mesh = [
    `radial-gradient(ellipse 95% 90% at ${18 + ox * 0.4}% ${32 + oy}%, ${rgba(rHi, gHi, bHi, aBlob2)} 0%, transparent 58%)`,
    `radial-gradient(ellipse 80% 95% at ${84 - ox * 0.3}% ${22 - oy * 0.5}%, ${rgba(r, g, b, aBlob * 1.05)} 0%, transparent 52%)`,
    `radial-gradient(ellipse 85% 75% at ${48 + ox}% ${92 + oy * 0.4}%, ${rgba(rLo, gLo, bLo, aBlob)} 0%, transparent 55%)`,
    `linear-gradient(128deg, ${rgba(r, g, b, aWash)} 0%, ${rgba(rHi, gHi, bHi, aWash * 0.92)} 38%, ${rgba(rLo, gLo, bLo, aWash * 0.96)} 100%)`,
  ].join(', ');

  const glow = rgba(r, g, b, isDark ? 0.35 : 0.28);

  return {
    backgroundImage: mesh,
    backgroundColor: accentHex || (isDark ? '#4b5563' : '#9ca3af'),
    boxShadow: `0 0 14px -2px ${glow}, 0 0 6px -2px ${rgba(rHi, gHi, bHi, 0.25)}`,
  };
}

interface CategoryWidgetProps {
  title: string;
  accentColor: string;
  bgTint: string;
  isDark: boolean;
}

const CategoryWidget: React.FC<CategoryWidgetProps> = ({ title, accentColor, bgTint, isDark }) => {
  const { ref, isXS, isSM } = useWidgetSize();
  const { items, stockUnits } = useInventoryStore();
  const { globalScopeWarehouseId } = useStockDashboardStore();

  const categories = useMemo(() => {
    const units = globalScopeWarehouseId
      ? stockUnits.filter(u => u.warehouseId === globalScopeWarehouseId && u.status !== 'retired')
      : stockUnits.filter(u => u.status !== 'retired');

    const itemById = new Map(items.map(i => [i.id, i]));

    const map = new Map<string, { total: number; inStock: number; checkedOut: number }>();
    for (const unit of units) {
      const cat = itemById.get(unit.itemId)?.category ?? 'Uncategorised';
      const entry = map.get(cat) ?? { total: 0, inStock: 0, checkedOut: 0 };
      entry.total++;
      if (unit.status === 'in stock')    entry.inStock++;
      if (unit.status === 'checked out') entry.checkedOut++;
      map.set(cat, entry);
    }

    return Array.from(map.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.total - a.total);
  }, [items, stockUnits, globalScopeWarehouseId]);

  const barH   = isXS ? 'h-1' : 'h-1.5';
  const nameC  = isXS ? 'text-[10px]' : isSM ? 'text-xs' : 'text-sm';
  const countC = isXS ? 'text-[10px]' : 'text-xs';
  const subC   = isXS ? 'text-[9px]'  : 'text-xs';
  const padC   = isXS ? 'p-2' : 'p-3';
  const headC  = isXS ? 'text-xs' : isSM ? 'text-sm' : 'text-base';
  const gapC   = isXS ? 'space-y-2' : 'space-y-3';

  return (
    <div
      ref={ref}
      className={`h-full flex flex-col ${padC} rounded border overflow-hidden`}
      style={{
        backgroundColor: bgTint || (isDark ? '#20242b' : '#ffffff'),
        borderColor: isDark ? '#343a43' : '#e5e7eb',
      }}
    >
      <h3 className={`${headC} font-semibold mb-2 flex-shrink-0 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
        {title}
      </h3>
      {categories.length === 0 ? (
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No stock data yet.</p>
      ) : (
        <div className={`${gapC} overflow-auto flex-1`}>
          {categories.map(cat => {
            const salt = cat.name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
            return (
            <div key={cat.name}>
              <div className={`flex items-center justify-between ${nameC} mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <span className="font-medium truncate mr-2">{cat.name}</span>
                {!isXS && <span className={`${countC} flex-shrink-0`}>{cat.total} total</span>}
              </div>
              <div
                className={`w-full ${barH} rounded-full overflow-visible ${isDark ? 'bg-[#2b3140]/90 ring-1 ring-white/[0.06]' : 'bg-gray-100 ring-1 ring-black/[0.04]'}`}
              >
                <div
                  className="h-full min-w-0 rounded-full transition-[width] duration-500 ease-out"
                  style={{
                    width: cat.total > 0 ? `${(cat.inStock / cat.total) * 100}%` : '0%',
                    ...auraMeshBarStyle(accentColor || (isDark ? '#60a5fa' : '#3b82f6'), isDark, salt),
                  }}
                />
              </div>
              {!isXS && (
                <div className={`${subC} mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {isSM
                    ? `${cat.inStock} in / ${cat.checkedOut} out`
                    : `In Stock: ${cat.inStock} · Checked Out: ${cat.checkedOut}`}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CategoryWidget;
